import { PDFViewer } from '@react-pdf/renderer';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  buildRetailInvoiceLabels,
  RetailInvoicePDF,
} from 'src/components/RetailInvoice';
import { BuyerCard } from 'src/components/Selectors/BuyerSelector/BuyerCard';
import { useAppContext } from 'src/contexts/AppContext';
import {
  useCatalogDataContextDataSelector,
  useCatalogDataContextEventsTransformedSelector,
} from 'src/contexts/CatalogDataContext';
import { useContentContext } from 'src/contexts/ContentContext';
import { useGetBuyerPaymentMethods } from 'src/hooks/api/useGetBuyerPaymentMethods';
import { useRetailSaleListingTickets } from 'src/hooks/api/useRetailSaleListingTickets';
import { useGetEventFullInfo } from 'src/hooks/useGetEventFullInfo';
import { EmptyListingQuery } from 'src/utils/eventQueryUtils';
import {
  BuyerWithSalesDto,
  EventWithData,
  ListingDetails,
  ListingQuery,
  MarketplacePaymentLineType,
  MarketplacePaymentState,
} from 'src/WebApiController';

import { buildOrderDetailRows } from '../buildOrderDetailRows';
import { useRetailSaleContext } from '../RetailSaleLocalContext';
import { RetailSaleFormValues } from '../types';
import * as styles from './RetailSaleInvoicePreviewStep.css';

type RetailSaleInvoicePreviewStepProps = {
  listing: ListingDetails | undefined;
  event: EventWithData | undefined;
};

export const RetailSaleInvoicePreviewStep = ({
  listing,
  event,
}: RetailSaleInvoicePreviewStepProps) => {
  const { loginContext } = useAppContext();
  // POS-8856: resolve invoice labels (component has ContentContext) to pass into the
  // PDF preview, which renders outside the provider tree via <PDFViewer>.
  const contentContext = useContentContext();
  const labels = buildRetailInvoiceLabels(contentContext);
  const { watch } = useFormContext<RetailSaleFormValues>();
  const values = watch();
  const { venue } = useGetEventFullInfo(event?.event ?? null);

  // POS-8630: multi-event assembly. Multi-listing mode when the form carries
  // per-listing selections. Hooks are called unconditionally; the listing
  // query no-ops (returns []) when the filter is null.
  const isMultiListing = (values.perListingSelections?.length ?? 0) > 0;

  const eventsTransformed = useCatalogDataContextEventsTransformedSelector();
  const catalogData = useCatalogDataContextDataSelector();

  const listingFilterQuery = useMemo((): ListingQuery | null => {
    const ids = values.allListingIds ?? [];
    if (!isMultiListing || ids.length === 0) return null;
    return { ...EmptyListingQuery, entityIds: ids };
  }, [isMultiListing, values.allListingIds]);

  const { data: listingTickets } =
    useRetailSaleListingTickets(listingFilterQuery);

  const { selectedStoredPaymentMethodId } = useRetailSaleContext();
  const { data: savedPaymentMethods } = useGetBuyerPaymentMethods(
    values.buyerEmail
  );

  const selectedPaymentMethod = savedPaymentMethods?.find(
    (pm) => pm.storedPaymentMethodId === selectedStoredPaymentMethodId
  );
  const paymentMethodDisplay = selectedPaymentMethod
    ? `${selectedPaymentMethod.cardBrand ?? ''} ****${selectedPaymentMethod.cardSummary ?? ''}`.trim()
    : undefined;

  const proceeds = values.totalNetProceeds ?? 0;
  const marketplaceLinesCost =
    values.marketplacePaymentLines?.reduce(
      (sum, item) => sum + (item.amount ?? 0),
      0
    ) ?? 0;
  const totalAmount = proceeds + marketplaceLinesCost;

  const paymentsMade = (values.marketplacePaymentLines ?? [])
    .filter(
      (line) =>
        line.paymentLineType === MarketplacePaymentLineType.Proceeds &&
        line.paymentState === MarketplacePaymentState.Paid
    )
    .reduce((sum, line) => sum + (line.amount ?? 0), 0);
  const balanceDue = totalAmount - paymentsMade;

  // POS-8630: build order detail rows (one per event) for the invoice.
  const orderDetailRows = useMemo(
    () =>
      buildOrderDetailRows({
        values,
        event,
        venue,
        totalAmount,
        listing,
        listingTickets,
        eventsTransformed,
        catalogData,
      }),
    [
      values,
      event,
      venue,
      totalAmount,
      listing,
      listingTickets,
      eventsTransformed,
      catalogData,
    ]
  );

  const invoiceNumber =
    values.externalSaleId ?? `${listing?.id ?? values.listingId}`;

  const buyerAddress = [
    values.shippingStreet,
    values.shippingCity,
    values.shippingState,
    values.shippingCountry,
  ]
    .filter(Boolean)
    .join(', ');

  const pdfProps = {
    brokerName: loginContext?.user?.activeAccount?.accountName ?? '',
    brokerEmail: loginContext?.user?.userName ?? undefined,
    invoiceNumber,
    invoiceDate: values.saleDate ?? new Date().toISOString(),
    currency: values.currencyCode ?? 'USD',
    paymentTermsDays: values.paymentTermsDays,
    lineItems: [
      ...(proceeds !== 0
        ? [
            {
              description: labels.ticketProceeds,
              amount: proceeds,
              costType: labels.proceeds as string,
            },
          ]
        : []),
      ...(values.marketplacePaymentLines ?? []).map((line) => ({
        description: line.description ?? '',
        ratePercent: line.ratePercent ?? undefined,
        amount: line.amount ?? 0,
        costType: ((line.amount ?? 0) < 0
          ? labels.credit
          : labels.charge) as string,
      })),
    ],
    buyerName: values.buyerName,
    buyerEmail: values.buyerEmail,
    buyerAddress: buyerAddress || undefined,
    paymentMethod: paymentMethodDisplay,
    totalAmount,
    balanceDue,
    paymentsMade,
    status: (values.paymentReceived === true ? 'Paid' : 'Outstanding') as
      | 'Paid'
      | 'Outstanding',
    orderDetailRows,
  };

  const eventName = event?.event?.name;
  const eventDate = event?.event?.dates?.start;
  const section = listing?.seating?.section ?? values.section;
  const row = listing?.seating?.row ?? values.row;
  const seatFr = listing?.seating?.seatFr ?? values.seatFrom;
  const seatTo = listing?.seating?.seatTo ?? values.seatTo;
  const ticketCount = listing?.availQty ?? values.quantitySold;

  const seatingDisplay = [
    section ? `Section ${section}` : null,
    row ? `Row ${row}` : null,
    seatFr && seatTo
      ? `Seats ${seatFr}–${seatTo}`
      : seatFr
        ? `Seat ${seatFr}`
        : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const buyerForCard: BuyerWithSalesDto = {
    buyerEmail: values.buyerEmail ?? '',
    buyerName: values.buyerName ?? null,
    buyerPhoneNumber: values.buyerPhone ?? null,
    internalNotes: null,
    lastRetailActivityDate: null,
    billingAddressLine1: values.billingStreet ?? null,
    billingAddressLine2: null,
    billingCity: values.billingCity ?? values.shippingCity ?? null,
    billingState: values.billingState ?? values.shippingState ?? null,
    billingZipCode: values.billingZipCode ?? values.shippingZipCode ?? null,
    billingCountry: values.billingCountry ?? values.shippingCountry ?? null,
    shippingAddressLine1: values.shippingStreet ?? null,
    shippingAddressLine2: null,
    shippingCity: values.shippingCity ?? null,
    shippingState: values.shippingState ?? null,
    shippingZipCode: values.shippingZipCode ?? null,
    shippingCountry: values.shippingCountry ?? null,
    cardBrand: selectedPaymentMethod?.cardBrand ?? null,
    cardSummary: selectedPaymentMethod?.cardSummary ?? null,
    expiryMonth: selectedPaymentMethod?.expiryMonth ?? null,
    expiryYear: selectedPaymentMethod?.expiryYear ?? null,
    saleIds: null,
  };

  return (
    <div className={styles.previewContainer}>
      {/* Customer Info Card */}
      {(values.buyerName || values.buyerEmail) && (
        <BuyerCard buyer={buyerForCard} />
      )}

      {/* PDF Viewer */}
      <div className={styles.pdfViewerContainer}>
        <PDFViewer width="100%" height="500px" showToolbar={false}>
          <RetailInvoicePDF {...pdfProps} labels={labels} />
        </PDFViewer>
      </div>
    </div>
  );
};
